import type { DeferredDownPaymentV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency } from "../../../../lib/money";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import SummaryTableModal from "../../components/SummaryTableModal";
import { splitDeferredCashDown } from "./Financial.mapping";

interface DeferredDownPaymentModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deferredDownPayment: DeferredDownPaymentV2;
  /** `financial.itemization.downPayment.cashDown`: the stored total of today's and the deferred cash down. */
  cashDown?: number;
  /** Date shown for the cash down collected today (the contract date). */
  cashDownDate?: string;
}

interface CashDownRow {
  key: string;
  description: string;
  amount?: number;
  date?: string;
}

const DEFERRED_ROW_COUNT = 2;

// Absent amounts render blank, never 0.00.
const formatBlankAmount = (amount?: number) => (amount === undefined ? "" : formatCurrency(amount));

// Read-only cash down breakdown (today plus two deferred installments), opened from the "Cash Down" link
function DeferredDownPaymentModal({
  open,
  onOpenChange,
  deferredDownPayment,
  cashDown,
  cashDownDate,
}: DeferredDownPaymentModalProperties) {
  const { t } = useTranslation();
  const entries = deferredDownPayment.entries ?? [];

  const deferredRows: CashDownRow[] = Array.from({ length: DEFERRED_ROW_COUNT }, (_, index) => ({
    key: `deferred-${index + 1}`,
    description: t("financial.deferredDownPaymentModal.deferredCashDown", { index: index + 1 }),
    amount: entries[index]?.amount,
    date: entries[index]?.date,
  }));

  // Cash Down is the stored total, so today's amount is whatever the deferred installments leave over.
  const { todayAmount } = splitDeferredCashDown(cashDown, entries.slice(0, DEFERRED_ROW_COUNT));

  const rows: CashDownRow[] = [
    {
      key: "today",
      description: t("financial.deferredDownPaymentModal.cashDownToday"),
      amount: todayAmount,
      date: cashDownDate,
    },
    ...deferredRows,
  ];

  const columns: Array<AuditTableColumn<CashDownRow>> = [
    { key: "description", header: t("financial.modalColumns.description"), render: (row) => row.description },
    {
      key: "amount",
      header: t("financial.modalColumns.amount"),
      render: (row) => formatBlankAmount(row.amount),
    },
    {
      key: "date",
      header: t("financial.modalColumns.date"),
      render: (row) => formatIsoDate(row.date),
    },
  ];

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.deferredDownPaymentModal.title")}
      description={t("financial.deferredDownPaymentModal.description")}
      caption={t("financial.deferredDownPaymentModal.caption")}
      summaryLines={[
        {
          label: t("financial.deferredDownPaymentModal.totalCashDown"),
          value: formatBlankAmount(cashDown),
        },
      ]}
      columns={columns}
      rows={rows}
      tableDensity="compact"
    />
  );
}

export default DeferredDownPaymentModal;
